import { LOG_GROUPS, LOG_LEVEL } from '@b2x/shared';

import {
  type AuthenticationState,
  IdentityProvider,
  getClaims,
  getIdentityProvider,
  isLoggedIn,
  isOnBehalfModeActive,
} from '@b2x/authentication-library';
import type { BusinessContext } from '../types/available-type';
import { BusinessType } from '../types/enums';
import type { Nullable, ProfileResponse, ProfileState } from '../types/profile-type';
import { EmptyProfile, STATUS } from '../utilities/constants';
import { getAvailableProfiles } from '../utilities/helpers';
import { logger } from '../utilities/logger';

export async function resolveAvailableProfiles(
  authState: AuthenticationState,
  profileState: ProfileState,
  businessContext: BusinessContext<BusinessType>,
): Promise<ProfileResponse> {
  const identityProvider = getIdentityProvider(authState);

  const [error, { profiles, meta }] = shouldFetchProfiles(authState, profileState)
    ? await getAvailableProfiles(authState)
    : [null, { profiles: profileState.availableProfiles, meta: profileState.meta }];

  if (error) {
    EmptyProfile.businessType = businessContext.businessType;
    return {
      profiles: [EmptyProfile],
      meta,
    };
  }

  if (identityProvider === IdentityProvider.BIAS && !isOnBehalfModeActive(authState) && isLoggedIn(authState)) {
    // add display name to B2C profile for SEM users while we don't get it from the bff
    const claims = getClaims(authState);
    const { given_name = '', family_name = '' } = claims || {};
    const displayName = `${given_name} ${family_name}`.trim();
    const semProfiles = profiles.map((profile) => {
      if (profile.businessType === BusinessType.B2C) {
        profile.displayName = displayName;
      }
      return profile;
    });

    // remove B2B profiles as they are not needed for SEM user, for now
    const filteredSemProfiles = semProfiles.filter((profile) => profile.businessType !== BusinessType.B2B);

    return {
      profiles: [...filteredSemProfiles],
      meta,
    };
  }

  return {
    profiles,
    meta,
  };
}

function shouldFetchProfiles(authState: AuthenticationState, profileState: Nullable<ProfileState>): boolean {
  // if user has visited a page where profile could change, we need to fetch the profiles to catch those possible changes
  // NOTE: this could happen when user does it themselves, or when SalesAgent is impersonating a user and visiting those pages
  // WARNING: this code is flawed as it will not work immediately after user changes profile, but it will work after a refresh
  // WARNING #2: this code does not cover the case when user changes profile in Attention module, until they refresh the page
  const profileChangingPage = [
    'my-area/my-settings',
    'my-area/business/create-company',
    'my-area/business/company-dashboard',
    'my-area/business/company-profile',
  ];
  const hasVisitedProfileChangingPage = profileChangingPage.find((path) => document.referrer.includes(path));
  const b2cProfile = profileState.availableProfiles?.find((profile) => profile.businessType === BusinessType.B2C);
  const isB2cProfileAuthenticated = Boolean(b2cProfile?.meId);
  const isSemAuthenticated = Boolean(b2cProfile?.displayName);

  if (!profileState.activeProfile) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'Profiles have not been initialized.'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  if (profileState.meta?.status === STATUS.FAILURE) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'Error fetching data.', profileState.meta.message],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  // @ts-expect-error: B2E has been removed but we still need to clean it up from the local and session storage.
  // By fetching the profiles again we ensure that both storages are updated with the correct profiles and business type.
  if (profileState.activeProfile.businessType === 'B2E') {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'Business type is B2E which no longer exists.'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  if (hasVisitedProfileChangingPage) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'User was on a page that could lead to profile change'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  // if user logs out but PL still has their profile
  if (!authState.jwe && (isSemAuthenticated || isB2cProfileAuthenticated)) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'User logged out, but PL still has their profile in the session.'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  switch (authState.identityProvider) {
    case IdentityProvider.CIAS:
      return shouldFetchProfilesForCiasCases(authState, isB2cProfileAuthenticated);
    case IdentityProvider.BIAS:
      return shouldFetchProfilesForBiasCases(authState, isB2cProfileAuthenticated, isSemAuthenticated);
    default:
      return false;
  }
}

function shouldFetchProfilesForCiasCases(authState: AuthenticationState, isB2cProfileAuthenticated: boolean): boolean {
  // if user logs in but PL does not have their profiles
  if (authState.jwe && !isB2cProfileAuthenticated) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'User logged in, but PL does not have their profiles in the session.'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  return false;
}

function shouldFetchProfilesForBiasCases(
  authState: AuthenticationState,
  isB2cProfileAuthenticated: boolean,
  isSemAuthenticated: boolean,
): boolean {
  const actingOnBehalf = isOnBehalfModeActive(authState);

  // SEM user starts on-behalf session, but PL still has SEM as the active profile
  if (actingOnBehalf && !isB2cProfileAuthenticated) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'Sales expert starts an on-behalf session'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  // SEM user stops on-behalf session, but PL still has SEM as the active profile
  if (!actingOnBehalf && isB2cProfileAuthenticated) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'Sales expert stops an on-behalf session'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  // if SEM user logs in but PL does not have their SEM profile
  if (authState.jwe && !isSemAuthenticated) {
    logger('Profiles will be fetched: ', {
      args: ['Reason:', 'User logged in, but PL does not have their SEM profile in the session.'],
      group: LOG_GROUPS.BOOTSTRAP,
      level: LOG_LEVEL.INFO,
    });
    return true;
  }

  return false;
}
